import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchAllAnimations, fetchMyAnimations, AnimationOverview } from '../../../backend/useAnimateList';
import { useUser } from '../../../backend/useUser';
import Gallery from '../../website/Gallery';
import { Project } from '../../website/ProjectGrid';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';

const GalleryContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: visible;
`;

interface AnimateGalleryProps {
    colormode: 'light' | 'dark';
    tabs: Array<{ name: string; icon?: string; disabled?: boolean }>
}

export const ANIMATIONS_PER_PAGE = 12;

const AnimateGallery: React.FC<AnimateGalleryProps> = ({ colormode, tabs }) => {
    const [activeTab, setActiveTab] = useState('My Animations');
    const { user, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const hasAttemptedInitialLoad = useRef(false);

    // Community Query
    const communityQuery = useInfiniteQuery({
        queryKey: ['animations', 'community'],
        queryFn: ({ pageParam = 0 }) => fetchAllAnimations(pageParam as number, ANIMATIONS_PER_PAGE),
        getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextOffset : undefined,
        initialPageParam: 0,
    });

    // My Animations Query
    const myAnimsQuery = useInfiniteQuery({
        queryKey: ['animations', 'my', user?.id],
        queryFn: ({ pageParam = 0 }) => {
            if (!user?.id) return { animations: [], hasMore: false, nextOffset: 0 };
            return fetchMyAnimations(user.id, pageParam as number, ANIMATIONS_PER_PAGE);
        },
        getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextOffset : undefined,
        initialPageParam: 0,
        enabled: !!user?.id,
    });

    useEffect(() => {
        if (!userLoading && !user) {
            setActiveTab('Community');
        }
    }, [user, userLoading]);

    const animToProject = useCallback((anim: AnimationOverview): Project => {
        const mediaUrl = anim.animationVideo || anim.referenceImage || null;
        return {
            id: anim.id,
            name: anim.animation_name || '',
            username: anim.user_tag || '',
            imageUrl: mediaUrl,
            linkType: 'animate',
        };
    }, []);

    // Flatten pages into a single array for the UI
    const communityProjects = useMemo(() => 
        communityQuery.data?.pages.flatMap(page => page.animations.map(animToProject)) || [], 
    [communityQuery.data, animToProject]);

    const myProjects = useMemo(() => 
        myAnimsQuery.data?.pages.flatMap(page => page.animations.map(animToProject)) || [], 
    [myAnimsQuery.data, animToProject]);

    // Switch tab to community if "My Animations" is empty on initial load
    useEffect(() => {
        if (
            !userLoading && 
            user && 
            activeTab === 'My Animations' && 
            !myAnimsQuery.isLoading && 
            myAnimsQuery.isSuccess && 
            !hasAttemptedInitialLoad.current
        ) {
            if (myProjects.length === 0) {
                setActiveTab('Community');
            }
            hasAttemptedInitialLoad.current = true;
        }
    }, [myProjects.length, myAnimsQuery.isLoading, myAnimsQuery.isSuccess, activeTab, user, userLoading]);

    const handleLoadMore = useCallback(async () => {
        if (activeTab === 'My Animations') {
            await myAnimsQuery.fetchNextPage();
        } else {
            await communityQuery.fetchNextPage();
        }
    }, [activeTab, myAnimsQuery, communityQuery]);

    const handleProjectClick = useCallback((project: Project) => {
        navigate(`/animate/${project.id}`);
    }, [navigate]);

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab);
    }, []);

    const visibleTabs = useMemo(() => {
        if (!user) return []; 
        return tabs;
    }, [user, tabs]);

    const currentProjects = activeTab === 'My Animations' ? myProjects : communityProjects;
    const currentLoading = activeTab === 'My Animations' ? myAnimsQuery.isFetching : communityQuery.isFetching;
    const currentHasMore = activeTab === 'My Animations' ? !!myAnimsQuery.hasNextPage : !!communityQuery.hasNextPage;

    if (userLoading) {
        return <LoadingSpinner colorMode={colormode} />;
    }

    return (
        <GalleryContainer>
            <Gallery
                type="Animation"
                colormode={colormode}
                projects={currentProjects}
                loading={currentLoading || userLoading}
                hasMore={currentHasMore}
                onLoadMore={handleLoadMore}
                onProjectClick={handleProjectClick}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabs={visibleTabs}
                userLoading={userLoading}
                pageSize={ANIMATIONS_PER_PAGE}
            />
        </GalleryContainer>
    );
};

export default AnimateGallery;